import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, LogOut, Inbox, MousePointerClick, Mail, ExternalLink } from "lucide-react";
import { authClient, getMe, logout } from "@/lib/auth";

function fmt(dateStr) {
  try { return format(new Date(dateStr), "MMM d, yyyy · h:mma"); } catch { return dateStr || "—"; }
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-accent">
        <Icon className="h-5 w-5" strokeWidth={1.6} />
        <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      </div>
      <div className="mt-4 font-display text-5xl text-primary leading-none">{value}</div>
    </div>
  );
}

export default function OwnerDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | authed | error
  const [user, setUser] = useState(location.state?.user || null);
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let u = location.state?.user;
        if (!u) u = await getMe();
        if (cancelled) return;
        setUser(u);
        const [s, inq] = await Promise.all([
          authClient.get("/owner/stats"),
          authClient.get("/owner/inquiries"),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setInquiries(inq.data);
        setStatus("authed");
      } catch (e) {
        if (!cancelled) navigate("/owner/login");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogout = async () => {
    try { await logout(); } catch (_) {}
    navigate("/owner/login");
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading dashboard...
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-display text-3xl text-foreground">Dashboard failed to load</p>
          <p className="mt-3 text-muted-foreground font-light">Something went wrong reaching the server. Please try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 rounded-full bg-primary px-7 py-3.5 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sources = stats?.clicks_by_source || {};
  const sourceEntries = Object.entries(sources).sort((a, b) => b[1] - a[1]);
  const maxSource = sourceEntries.length ? Math.max(...sourceEntries.map(([, v]) => v)) : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="owner-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-medium">PK Lake House</p>
            <h1 className="font-display text-2xl text-foreground leading-none">Owner Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              View site <ExternalLink className="h-3.5 w-3.5" />
            </a>
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <span className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center font-display">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
            <button
              onClick={onLogout}
              data-testid="owner-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.6} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="grid sm:grid-cols-3 gap-5">
          <StatCard icon={Inbox} label="Total inquiries" value={stats?.total_inquiries ?? 0} testid="stat-total-inquiries" />
          <StatCard icon={MousePointerClick} label="VRBO click-throughs" value={stats?.total_vrbo_clicks ?? 0} testid="stat-total-clicks" />
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="stat-sources">
            <div className="flex items-center gap-2 text-accent">
              <MousePointerClick className="h-5 w-5" strokeWidth={1.6} />
              <span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Clicks by section</span>
            </div>
            <div className="mt-4 space-y-2">
              {sourceEntries.length === 0 && <p className="text-sm text-muted-foreground">No clicks yet.</p>}
              {sourceEntries.map(([src, count]) => (
                <div key={src} className="flex items-center gap-3">
                  <span className="w-20 text-xs text-muted-foreground capitalize truncate">{src}</span>
                  <span className="h-2 rounded-full bg-accent" style={{ width: `${maxSource ? (count / maxSource) * 100 : 0}%`, minWidth: 6 }} />
                  <span className="text-xs text-foreground ml-auto">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-3xl text-foreground">Inquiries</h2>
            <span className="text-sm text-muted-foreground">{inquiries.length} total</span>
          </div>

          <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="inquiries-table">
            {inquiries.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">
                No inquiries yet. Requests from the site's contact form will appear here.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                      <th className="px-5 py-3 font-medium">Guest</th>
                      <th className="px-5 py-3 font-medium">Dates</th>
                      <th className="px-5 py-3 font-medium">Guests</th>
                      <th className="px-5 py-3 font-medium">Message</th>
                      <th className="px-5 py-3 font-medium">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((q) => (
                      <tr key={q.id} className="border-b border-border/60 last:border-0 align-top">
                        <td className="px-5 py-4">
                          <div className="font-medium text-foreground">{q.name}</div>
                          <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1 text-accent hover:underline">
                            <Mail className="h-3 w-3" /> {q.email}
                          </a>
                          {q.phone && <div className="text-muted-foreground">{q.phone}</div>}
                        </td>
                        <td className="px-5 py-4 text-foreground whitespace-nowrap">
                          {q.check_in || "—"}<br />
                          <span className="text-muted-foreground">{q.check_out ? `→ ${q.check_out}` : ""}</span>
                        </td>
                        <td className="px-5 py-4 text-foreground">{q.guests || "—"}</td>
                        <td className="px-5 py-4 text-muted-foreground max-w-xs">{q.message || "—"}</td>
                        <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{fmt(q.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
